import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Snackbar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddStudentDialog from 'src/components/dialogs/student/addStudentDialog';

import { cyrillicToLatin } from 'src/helper/helperFunction';
import { API_BASE_URL } from 'src/apiConfig';
import { calculatePoints } from './calculatePoints';
import StudentInfoBox from './Components/StudentInfoBox';
import StudentImage from './Components/StudentImage';
import StudentList from './Components/StudentList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ReviewComponent = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [result, setResult] = useState('');
  const [points, setPoints] = useState(0);

  const query = useQuery();
  const schoolId = query.get('schoolId');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const username = localStorage.getItem('username') || '';
  const [currentTab, setCurrentTab] = useState(0);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [corectSolutions, setCorectSolutions] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  //podesvanja for checboxes
  const [marginTopOffset, setMarginTopOffset] = useState(0);
  const [marginLeftOffset, setMarginLeftOffset] = useState(0);
  const [opacity, setOpacity] = useState(0.7);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(
        `${API_BASE_URL}/api/students.php?ConnectedStudentsASCBySchoolId=${schoolId}`
      )
      .then((response) => {
        setStudents(response.data);

        const unreviewedStudents = response.data.filter(
          (student) => Number(student.isReviewed) === 0
        );

        if (unreviewedStudents.length > 0) {
          setSelectedStudentId(unreviewedStudents[0].id);
          setCurrentTab(0); // Nepregledani tab
        } else {
          const reviewedStudents = response.data.filter(
            (student) => Number(student.isReviewed) === 1
          );
          if (reviewedStudents.length > 0) {
            setSelectedStudentId(reviewedStudents[0].id);
            setCurrentTab(1); // Pregledani tab
          } else {
            // Ako nema učenika uopšte
            setSelectedStudentId(null);
            setStudentData(null);
          }
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error('There was an error fetching the students!', error);
        setLoading(false);
      });
  }, [schoolId]);

  // uzimanje info o konkretnom uceniku
  useEffect(() => {
    if (selectedStudentId) {
      axios
        .get(
          `${API_BASE_URL}/api/reviewAll.php?action=getStudentInfoFinalResult&idStudent=${selectedStudentId}`
        )
        .then((response) => {
          setStudentData(response.data);
          setResult(response.data.result);
          fetchSolutions(response.data.class).then((correctSolutions) => {
            calculateAndSetPoints(response.data.result ?? '', correctSolutions);
          });
        })
        .catch((error) => {
          console.error('There was an error fetching the student data!', error);
        });
    } else {
      setStudentData(null);
    }
  }, [selectedStudentId]);

  const allStudentsReviewed =
    students.length > 0 &&
    students.every((student) => Number(student.isReviewed) === 1);

  const handleFinishReviewing = () => {
    axios
      .post(`${API_BASE_URL}/api/schools.php`, {
        action: 'finishReviewingSchool',
        schoolId: schoolId
      })
      .then((response) => {
        if (response.data.success) {
          navigate('/review-schools?step=1');
        } else {
          alert('Došlo je do greške prilikom završavanja pregledanja.');
        }
      })
      .catch((error) => {
        console.error('There was an error finishing the review!', error);
      });
  };

  // kada se menja checkbox
  const handleCheckboxChange = (questionNumber, letter) => {
    const answer = `${questionNumber}${letter}`;

    // Kreiranje novog rezultata bez dvotački na početku/kraju
    let newResult = result ? result.split(':').filter(Boolean) : [];

    if (newResult.includes(answer)) {
      // Ako odgovor postoji, ukloni ga
      newResult = newResult.filter((item) => item !== answer);
    } else {
      // Ako odgovor ne postoji, dodaj ga
      newResult.push(answer);
    }

    // Kreiraj string iz nove liste rezultata sa tačno jednim dvotačkom između
    const finalResult = newResult.join(':');

    // Ažuriraj stanje
    setResult(finalResult);

    // Ponovo izračunaj poene sa novim odgovorom
    calculateAndSetPoints(finalResult, corectSolutions);
  };

  // uzimanje tacnih odgovora za odredjeni razred
  const fetchSolutions = async (gradeClass) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/gradeSolutions.php?class=${gradeClass}`
      );
      setCorectSolutions(response.data);
      return response.data;
    } catch (error) {
      console.error(t('solutions.errorFetchingSolutions'), error);
      return '';
    }
  };

  const calculateAndSetPoints = (newResult, correctSolutions) => {
    const { totalPoints } = calculatePoints(correctSolutions, newResult);
    setPoints(totalPoints);
  };

  const handleSave = () => {
    axios
      .post(`${API_BASE_URL}/api/reviewAll.php`, {
        action: 'saveResult',
        studentId: selectedStudentId,
        points: points,
        result,
        isReviewed: 1
      })
      .then((response) => {
        if (!response.data.success) return;
        handleConfirmReview();

        axios
          .get(
            `${API_BASE_URL}/api/students.php?ConnectedStudentsASCBySchoolId=${schoolId}`
          )
          .then((response) => {
            setStudents(response.data);

            const filteredStudents = response.data.filter((student) => {
              if (currentTab === 0) {
                return Number(student.isReviewed) === 0;
              } else {
                return Number(student.isReviewed) === 1;
              }
            });

            if (filteredStudents.length > 0) {
              // Ako ima učenika, prikaži prvog
              setSelectedStudentId(filteredStudents[0].id);
            } else {
              // Ako nema učenika u trenutnom tabu, automatski promeni na drugi tab
              const oppositeTabStudents = response.data.filter((student) => {
                if (currentTab === 0) {
                  return Number(student.isReviewed) === 1;
                } else {
                  return Number(student.isReviewed) === 0;
                }
              });

              if (oppositeTabStudents.length > 0) {
                // Ako ima učenika u suprotnom tabu, prebaci se na taj tab i prikaži prvog učenika
                setCurrentTab(currentTab === 0 ? 1 : 0);
                if (oppositeTabStudents[0].id !== selectedStudentId) {
                  setSelectedStudentId(oppositeTabStudents[0].id);
                }
              } else {
                // Ako ni u suprotnom tabu nema učenika, postavi odabranog učenika na null
                setSelectedStudentId(null);
                setStudentData(null);
              }
            }
          })
          .catch((error) => {
            console.error('There was an error refreshing the students!', error);
          });
      })
      .catch((error) => {
        console.error('There was an error saving the results!', error);
      });
  };

  // Modifikovana handleTabChange funkcija
  const handleTabChange = (event, newValue) => {
    const filteredStudents = students.filter((student) => {
      if (newValue === 0) {
        return Number(student.isReviewed) === 0;
      } else {
        return Number(student.isReviewed) === 1;
      }
    });

    if (filteredStudents.length > 0) {
      // Ako ima učenika, prikaži prvog i promeni tab
      setSelectedStudentId(filteredStudents[0].id);
      setCurrentTab(newValue);
    } else {
      // Ako nema učenika, prikaži Snackbar poruku i zadrži trenutni tab
      setSnackbarMessage(
        newValue === 0
          ? t('review.noUncheckedStudents')
          : t('review.noCheckedStudents')
      );

      setSnackbarOpen(true);
    }
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };
  const handleOpacityIncrease = () => {
    setOpacity((prev) => Math.min(prev + 0.1, 1));
  };

  const handleOpacityDecrease = () => {
    setOpacity((prev) => Math.max(prev - 0.1, 0));
  };

  const handleMoveUp = () => {
    setMarginTopOffset((prev) => prev - 1);
  };
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleMoveDown = () => {
    setMarginTopOffset((prev) => prev + 1);
  };

  const handleMoveLeft = () => {
    setMarginLeftOffset((prev) => prev - 1);
  };

  const handleMoveRight = () => {
    setMarginLeftOffset((prev) => prev + 1);
  };
  const handleStudentChange = (id) => {
    setSelectedStudentId(id);
  };
  const handleEditStudent = () => {
    handleDialogToggle('student', true);
  };

  const handleDialogToggle = (dialogType, openStatus) => {
    setOpenStudentDialog(openStatus);
  };
  // Navigacija nazad
  const handleBackClick = () => {
    navigate('/review-schools?step=1'); // Navigacija na drugi korak steppera
  };

  const handleAddStudentSuccess = (updatedData) => {
    axios
      .get(
        `${API_BASE_URL}/api/reviewAll.php?action=getStudentInfoFinalResult&idStudent=${selectedStudentId}`
      )
      .then((response) => {
        setStudentData(response.data);
        setResult(response.data.result);
        fetchSolutions(response.data.class).then((correctSolutions) => {
          calculateAndSetPoints(response.data.result, correctSolutions);
        });
      })
      .catch((error) => {
        console.error(
          'There was an error fetching the updated student data!',
          error
        );
      });

    // Osveži celu listu učenika
    axios
      .get(
        `${API_BASE_URL}/api/students.php?ConnectedStudentsASCBySchoolId=${schoolId}`
      )
      .then((response) => {
        setStudents(response.data);
      })
      .catch((error) => {
        console.error('There was an error refreshing the student list!', error);
      });
  };

  // reset pregledanja
  const handleResetReview = () => {
    axios
      .post(`${API_BASE_URL}/api/reviewAll.php`, {
        action: 'saveResult',
        studentId: selectedStudentId,
        points: 0,
        result,
        isReviewed: 0
      })
      .then(() => {
        handleCloseConfirmDialog();

        axios
          .get(
            `${API_BASE_URL}/api/students.php?ConnectedStudentsASCBySchoolId=${schoolId}`
          )
          .then((response) => {
            setStudents(response.data);

            const filteredStudents = response.data.filter((student) => {
              if (currentTab === 0) {
                return Number(student.isReviewed) === 0;
              } else {
                return Number(student.isReviewed) === 1;
              }
            });

            if (filteredStudents.length > 0) {
              setSelectedStudentId(filteredStudents[0].id);
            } else {
              setSelectedStudentId(null);
              setStudentData(null);
            }
          })
          .catch((error) => {
            console.error('There was an error refreshing the students!', error);
          });
      })
      .catch((error) => {
        console.error('There was an error resetting the results!', error);
      });
  };

  const handleConfirmReview = () => {
    const updatedStudents = students.map((student) =>
      student.id === selectedStudentId
        ? { ...student, points: points, point_sum: points, isReviewed: 1 }
        : student
    );

    // Pronađi sledećeg nepregledanog učenika
    const nextUnreviewed = updatedStudents.find(
      (student) => Number(student.isReviewed) === 0
    );

    if (nextUnreviewed) {
      setSelectedStudentId(nextUnreviewed.id);
    } else {
      setSelectedStudentId(updatedStudents[0].id); // Ako nema nepregledanih, prikazati prvog učenika
    }

    setStudents(updatedStudents);
  };

  // reset all
  const clearAllCheckboxes = () => {
    setResult(''); // Poništi sve odgovore
    calculateAndSetPoints('', corectSolutions); // Resetuj poene
  };

  const renderCheckboxes = () => {
    if (!studentData || !studentData.solutions) return null;

    const results = result.split(':').filter(Boolean);
    const positions = [];

    const baseTop = 35.5;
    const baseLeft = 18;

    for (let i = 1; i <= 15; i++) {
      const rowOffset = baseTop + (i - 1) * 3.3;
      const adjustedTop = rowOffset + Math.floor((i - 1) / 5) * 5.3;

      ['A', 'B', 'V', 'G', 'D'].forEach((letter, idx) => {
        positions.push({
          questionNumber: i,
          letter,
          top: `${adjustedTop}%`,
          left: `${baseLeft + 3.5 + idx * 4.6}%`,
          checked: results.includes(`${i}${letter}`)
        });
      });
    }

    for (let i = 16; i <= 30; i++) {
      const rowOffset = baseTop + (i - 16) * 3.3;
      const adjustedTop = rowOffset + Math.floor((i - 16) / 5) * 5.2;

      ['A', 'B', 'V', 'G', 'D'].forEach((letter, idx) => {
        positions.push({
          questionNumber: i,
          letter,
          top: `${adjustedTop}%`,
          left: `${66 + idx * 4.6}%`,
          checked: results.includes(`${i}${letter}`)
        });
      });
    }

    return positions.map((pos, idx) => (
      <input
        key={idx}
        type="checkbox"
        checked={pos.checked}
        onChange={() => handleCheckboxChange(pos.questionNumber, pos.letter)}
        disabled={Number(studentData.isReviewed) === 1}
        style={{
          position: 'absolute',
          top: `calc(${pos.top} + ${marginTopOffset}px)`,
          left: `calc(${pos.left} + ${marginLeftOffset}px)`,
          opacity: opacity,
          transform: 'scale(1.3)',
          width: 15,
          height: 15,
          backgroundColor:
            Number(studentData.point_sum) !== 0 ? '#b0c4de' : 'black',
          borderColor: Number(studentData.point_sum) !== 0 ? '#8fa8c8' : 'black'
        }}
      />
    ));
  };

  if (loading) {
    return <CircularProgress />;
  }
  //TODO
  // if (!studentData) {
  //   return (
  //     <Box
  //       display="flex"
  //       flexDirection="column"
  //       justifyContent="center"
  //       alignItems="center"
  //       height="70vh"
  //       textAlign="center"
  //     >
  //       <Typography
  //         variant="h4"
  //         color="primary"
  //         fontWeight="bold"
  //         style={{
  //           padding: '20px',
  //           borderRadius: '8px'
  //         }}
  //       >
  //         Nema učenika za ovu školu
  //       </Typography>
  //       <img
  //         src={ImageUrl.noData2}
  //         alt="No data"
  //         style={{ width: '300px', height: '200px', marginTop: '20px' }}
  //       />
  //     </Box>
  //   );
  // }

  const filteredStudents = students
    .filter((student) => {
      if (currentTab === 0) {
        return Number(student.isReviewed) === 0;
      } else {
        return Number(student.isReviewed) === 1;
      }
    })
    .filter((student) => {
      const normalizedSearchTerm = cyrillicToLatin(searchTerm.toLowerCase());
      const normalizedStudentName = cyrillicToLatin(student.name.toLowerCase());
      return normalizedStudentName.includes(normalizedSearchTerm);
    });

  return (
    <Grid container spacing={2} justifyContent="center">
      {allStudentsReviewed && (
        <Paper
          style={{
            backgroundColor: '#e3f2fd',
            // padding: '5px',
            borderRadius: '5px',
            // paddingBottom: '10px',
            marginLeft: '-180px'
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            maxWidth="600px"
          >
            <Alert
              severity="info"
              style={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                fontSize: '12px',
                fontWeight: '500',
                textAlign: 'center',
                // padding: '10px 10px',
                borderRadius: '8px'
              }}
            >
              {t('review.text')}
            </Alert>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleFinishReviewing}
              style={{
                backgroundColor: '#d21919',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '6px',
                fontSize: '10px',
                marginBottom: '5px'
              }}
            >
              {t('review.finishReview')}
            </Button>
          </Box>
        </Paper>
      )}
      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleBackClick}
          aria-hidden={false}
          style={{
            marginBottom: '10px',
            backgroundColor: '#0003',
            color: '#fff',
            borderRadius: '20%',
            minWidth: '40px',
            minHeight: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer'
          }}
        >
          <ArrowBackIcon />
        </Button>
      </Grid>
      <Grid item xs={3}>
        <StudentList
          currentTab={currentTab}
          handleTabChange={handleTabChange}
          t={t}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredStudents={filteredStudents}
          selectedStudentId={selectedStudentId}
          handleStudentChange={handleStudentChange}
        />
      </Grid>

      <Grid item xs={5}>
        <StudentImage
          studentData={studentData}
          t={t}
          API_BASE_URL={API_BASE_URL}
          renderCheckboxes={renderCheckboxes}
        />
      </Grid>

      <Grid item xs={4}>
        <StudentInfoBox
          t={t}
          studentData={studentData}
          username={username}
          points={points}
          opacity={opacity}
          handleEditStudent={handleEditStudent}
          handleSave={handleSave}
          currentTab={currentTab}
          openConfirmDialog={openConfirmDialog}
          setOpenConfirmDialog={setOpenConfirmDialog}
          handleOpacityDecrease={handleOpacityDecrease}
          handleOpacityIncrease={handleOpacityIncrease}
          handleMoveUp={handleMoveUp}
          handleMoveDown={handleMoveDown}
          handleMoveLeft={handleMoveLeft}
          handleMoveRight={handleMoveRight}
          handleResetReview={handleResetReview}
          clearAllCheckboxes={clearAllCheckboxes}
        />

        <Dialog
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
        >
          <DialogTitle>{t('review.confirmResetTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('review.confirmResetMessage')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
              {t('review.cancel')}
            </Button>
            <Button onClick={handleResetReview} color="secondary" autoFocus>
              {t('review.yes')}
            </Button>
          </DialogActions>
        </Dialog>

        <AddStudentDialog
          key={
            selectedStudentId ? selectedStudentId + new Date().getTime() : 'new'
          }
          open={openStudentDialog}
          handleClose={() => handleDialogToggle('student', false)}
          studentData={studentData}
          onSuccess={handleAddStudentSuccess}
        />
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000} // Trajanje prikaza Snackbar-a
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ReviewComponent;
